"use client";

import React, { useEffect, useRef, useState } from "react";
import { Globe, ShieldCheck, Cpu } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const features = [{
  title: "Gestão de Inventário",
  description: "Visibilidade total do seu estoque em tempo real, permitindo uma reposição inteligente e evitando rupturas ou excessos desnecessários em todas as suas unidades de venda.",
  icon: Globe
}, {
  title: "Conformidade Fiscal",
  description: "Automatize processos tributários complexos e garanta a emissão correta de documentos fiscais, mantendo sua operação varejista sempre segura e dentro das normas vigentes.",
  icon: ShieldCheck
}, {
  title: "Omnichannel Integrado",
  description: "Proporcione uma experiência de compra unificada, permitindo que seu cliente compre online e retire na loja, com integração total de dados, estoque e logística reversa.",
  icon: Cpu
}];
export default function KeyServiceFeatures() {
  const sectionRef = useRef<HTMLElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const {
    ref: revealRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const sectionHeight = rect.height;
      const start = windowHeight * 0.8;
      const end = -sectionHeight * 0.2;
      let progress = (start - rect.top) / (start - end);
      progress = Math.max(0, Math.min(1, progress));
      setScrollProgress(progress);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="41276" id="funcionalidades-principais" ref={sectionRef} className="relative w-full bg-background py-24 overflow-hidden">
      <style dangerouslySetInnerHTML={{
      __html: `
        :root {
          --fume-bg: hsla(0, 0%, 15%, 0.7);
          --background: var(--fume-bg);
        }
        section, .card, .popover, .modal, .bg-card, .bg-popover, .bg-modal, .bg-section {
          background-color: var(--fume-bg) !important;
          backdrop-filter: blur(12px) !important;
        }
        section *, .card *, .popover *, .modal * {
          color: var(--foreground);
        }
      `
    }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[40%_60%] gap-12 lg:gap-20 items-start">
          
          {}
          <div ref={revealRef} className="flex flex-col space-y-8 relative">
            {}
            <div className="absolute left-[11px] top-2 w-[1px] bg-primary/30 hidden lg:block transition-all duration-75 ease-linear" style={{
            height: `${scrollProgress * 100}%`,
            maxHeight: '100%'
          }} />

            {features.map((feature, i) => <div key={i} data-index={i} className={cn("relative pl-12 transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                {}
                <div className="absolute left-0 top-1 bg-accent pb-2 z-10 rounded-sm">
                  <feature.icon strokeWidth={1.5} className="w-6 h-6 text-primary" />
                </div>

                {}
                <div className="space-y-3">
                  <h3 className="font-serif text-[40px] leading-tight text-foreground">
                    {feature.title}
                  </h3>
                  <p className="font-sans text-[16px] leading-relaxed text-muted-foreground">
                    {feature.description}
                  </p>
                </div>
              </div>)}
          </div>

          {}
          <div className="card relative h-[600px] w-full rounded-[10px] overflow-hidden group border border-border">
            {}
            <div className="absolute inset-0 bg-primary/5 border border-white/10 backdrop-blur-[2px] z-10 pointer-events-none mix-blend-normal" />
            
            {}
            <div className="absolute inset-0 w-full h-[120%] -top-[10%] bg-cover bg-center transition-transform duration-100 ease-linear" style={{
            backgroundImage: 'url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592892/img/retail_seamless_environment.png)',
            transform: `translateY(${(scrollProgress - 0.5) * 40}px)`
          }} />

            {}
            <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent z-20" />

            {}
            <div className="absolute bottom-8 left-8 z-30 max-w-xs">
              <div className="h-[1px] w-12 bg-primary mb-4" />
              <p className="font-serif text-foreground text-lg italic">
                "Tecnologia que impulsiona o varejo moderno."
              </p>
            </div>
          </div>

        </div>
      </div>
    </section>;
}